import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./TextInput.module.css";

/**
 * TextInput
 *  type ['text', 'email', 'password', 'number', 'color', 'search', 'tel'] 를 지원함
 * @type React.FC<TextInputPropTypes>
 */
const TextInput = React.forwardRef((props, ref) => {
  Object.keys(props).forEach((p) => {
    if (!Object.keys(TextInputPropTypes).includes(p)) {
      console.error(`TextInput: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    /** 값 삭제(초기화) */
    clear() {
      setCurrState((prev) => ({
        ...prev,
        currValue: props.defaultValue ?? "",
        internalValue: props.internalValue ?? null,
        disabled: props.disabled,
        readOnly: props.readOnly,
      }));
    },
    /** 값 가져오기 */
    getValue() {
      return currState.internalValue ?? currState.currValue;
    },
    /** 내부 값 가져오기 */
    getInternalValue() {
      return currState.internalValue;
    },
    /** 값 지정 */
    setValue(v) {
      setCurrState((prev) => ({
        ...prev,
        currValue: v !== null ? v : "",
      }));
    },
    /** 내부 값 지정 */
    setInternalValue(v) {
      setCurrState((prev) => ({
        ...prev,
        internalValue: v,
      }));
    },
    /** 현재 input에 focus 처리 */
    focus() {
      if (!refInput.current) {
        console.warn("TextInput: refInput 가 아직 준비되지 않았습니다.");
      }
      refInput.current?.focus();
    },
    enable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: false,
      }));
    },
    disable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: true,
      }));
    },
    setReadOnly: (isReadOnly) => {
      setCurrState((prev) => ({
        ...prev,
        readOnly: isReadOnly,
      }));
    },
    setStyle: (style) => {
      setCurrState((prev) => ({
        ...prev,
        style: style,
      }));
    },
    updateState(stateName, newValue) {
      // currState에 있는 값만 업데이트 가능
      if (Object.keys(currState).includes(stateName)) {
        setCurrState((prev) => ({
          ...prev,
          [stateName]: newValue,
        }));
      } else {
        console.error(
          "TextInput: State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다."
        );
      }
    },
  }));

  const refInput = useRef();

  /**
   * TextInput Component의 State
   */
  const [currState, setCurrState] = useState({
    disabled: props.disabled,
    readOnly: props.readOnly,
    currValue: props.defaultValue,
    internalValue: props.internalValue,
    placeholder: props.placeholder,
    minLength: props.minLength,
    maxLength: props.maxLength,
    min: props.min,
    max: props.max,
    style: props.style,
  });

  /**
   * 변경 이벤트
   */
  const _onChange = (e) => {
    const { value } = e.target;

    setCurrState((prev) => {
      const updatedState = { ...prev, currValue: value };

      if (props.onChange) props.onChange(e, updatedState);

      return updatedState;
    });
  };

  /**
   * 키 입력 이벤트
   */
  const _onKeyUp = (e) => {
    if (e.key === "Enter") {
      if (props.onEnterPress) {
        props.onEnterPress(e);
      }
    } else {
      if (props.onKeyUp) {
        props.onKeyUp(e);
      }
    }
  };

  const _onKeyDown = (e) => {
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  return (
    <>
      <div
        className={`${styles.container} ${
          props.type === "range" && `${styles.flexColumn}`
        } wpct_100`}
      >
        <input
          className={`${styles.e3Input} ${props.controlWidth ?? ""} ${
            props.className ?? ""
          }`}
          type={props.type}
          ref={refInput}
          style={currState.style}
          disabled={currState.disabled}
          readOnly={currState.readOnly}
          value={currState.currValue}
          placeholder={currState.placeholder}
          {...(currState.minLength && { minLength: currState.minLength })}
          {...(currState.maxLength && { maxLength: currState.maxLength })}
          {...(currState.min && { min: currState.min })}
          {...(currState.max && { max: currState.max })}
          onChange={_onChange}
          onKeyDown={_onKeyDown}
          onKeyUp={_onKeyUp}
          {...(props.type === "range" && { min: currState.min })}
          {...(props.type === "range" && { max: currState.max })}
          {...(props.type === "range" && { step: props.step })}
        />
        {props.tip && (
          <span className={styles.tip}>
            <p>{props.tip}</p>
          </span>
        )}

        {props.type === "range" ? (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span className={styles.range_value}>{currState.min}</span>
            <span id="temp-value" className={styles.range_value}>
              {currState.currValue}
            </span>
            <span className={styles.range_value}>{currState.max}</span>
          </div>
        ) : null}
      </div>
    </>
  );
});

const TextInputPropTypes = {
  /**
   * type: input type https://developer.mozilla.org/ko/docs/Web/HTML/Element/input#input_%EC%9C%A0%ED%98%95
   *  - text: 일반 텍스트
   *  - email: email 유형 텍스트 (키보드 이메일에 맞게 전환)
   *  - password: password 유형 텍스트 (자동 텍스트 숨김)
   *  - number: 숫자 스피너 표기. min, max 지정 가능
   *  - color: 컬러 피커
   *  - search: 캐시에 저장 기능 있음
   *  - tel: 전화번호
   *  - url: url 텍스트 (텍스트입력처럼 보이지만 검증 매개변수가 있음)
   *
   */
  type: PropTypes.oneOf([
    "text",
    "email",
    "password",
    "number",
    "color",
    "search",
    "tel",
    "range",
  ]),
  /**
   * disabled: 비활성화
   * 기본값 false
   * updateState('disabled', bool) 함수로 수정 가능
   */
  disabled: PropTypes.bool,
  /**
   * readOnly: 읽기전용
   * 기본값 false
   * updateState('readOnly', bool) 함수로 수정 가능
   */
  readOnly: PropTypes.bool,

  /**
   * defaultValue: 최초 바인딩 되는 값
   * setValue()를 이용해 value 값 수정 가능
   */
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * internalValue: 내부적으로 관리할 값
   */
  internalValue: PropTypes.object,
  /**
   * 값이 비어있을 때 나타나는 내용
   * 기본값 없음
   * updateState('placeholder', string) 함수로 수정 가능
   */
  placeholder: PropTypes.string,
  /**
   * minLength: 텍스트 최소길이
   * 기본값 없음
   * updateState('minLength', number) 함수로 수정 가능
   */
  minLength: PropTypes.number,
  /**
   * maxLength: 텍스트 최대길이
   * 기본값 없음
   * updateState('maxLength', number) 함수로 수정 가능
   */
  maxLength: PropTypes.number,
  /**
   * min: type이 ['number','range'] 인 경우에만 유효. 최소값
   * 기본값 없음
   * updateState('min', number) 함수로 수정 가능
   */
  min: PropTypes.number,
  /**
   * max: type이 ['number','range'] 인 경우에만 유효. 최대값
   * 기본값 없음
   * updateState('max', number) 함수로 수정 가능
   */
  max: PropTypes.number,
  /**
   * step: type이 ['number','range'] 인 경우에만 유효. 최대값
   * 기본값 없음
   */
  step: PropTypes.number,
  /**
   * 변경 이벤트
   */
  onChange: PropTypes.func,
  /**
   * 엔터키 입력 이벤트
   */
  onEnterPress: PropTypes.func,
  /**
   * 키 입력 이벤트
   */
  onKeyUp: PropTypes.func,
  /**
   *
   */
  onKeyDown: PropTypes.func,
  /** */
  controlWidth: PropTypes.string,
  /**
   * className
   */
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  /**
   * style
   */
  style: PropTypes.object,
  /**
   * hidden
   */
  hidden: PropTypes.bool,
  addAll: PropTypes.bool,
};

TextInput.propTypes = TextInputPropTypes;
TextInput.defaultProps = {
  type: "text",
  disabled: false,
  readOnly: false,
  defaultValue: "",
  internalValue: null,
  placeholder: "",
  minLength: null,
  maxLength: null,
  min: null,
  max: null,
  step: null,
  onChange: null,
  onEnterPress: null,
  onKeyUp: null,
  onKeyDown: null,
  controlWidth: null,
  className: null,
  style: null,
};
TextInput.displayName = "TextInput";
export default TextInput;
